import { Col, Input, Layout, Modal, Row, Select } from 'antd';
import { get, omit, startCase } from 'lodash';
import queryString from 'query-string';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import RentStockServices from 'services/rent-stock';
import WarehouseServices from 'services/warehouse';

import PdButton from 'components/pd-button';
import PdCsvDownloader from 'components/pd-csv-downloader';
import { PdCsvUploader } from 'components/pd-csv-uploader';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import { errHandling } from 'utils';

import { StockCreateEdit } from './create-edit';
import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { confirm } = Modal;

export function RentStock() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(true);
  const [modalContent, setModalContent] = useState({});
  const [nextPage, setNextPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const buttonAction = (row) => (
    <Row className="button-table">
      <Col span={12} className="button">
        <PdButton
          className="height-32"
          type="danger"
          full
          onClick={handleDeleteStock.bind(this, row.id)}
          outline>
          Delete
        </PdButton>
      </Col>
      <Col span={12} className="button">
        <PdButton
          className="height-32"
          type="primary"
          full
          onClick={handleEditStock.bind(this, row)}>
          Edit
        </PdButton>
      </Col>
    </Row>
  );

  const skuCell = (row) => {
    const { product = {}, product_color = {} } = row;
    const { sku: productColorSku = '' } = product_color;
    const { sku: productNoColorSku = '' } = product;
    return productColorSku || productNoColorSku;
  };

  const columns = [
    {
      name: 'Warehouse',
      selector: row => row.warehouse.title,
      width: '20%',
      center: true,
    },
    {
      name: 'Product',
      selector: row => row.product.title,
      width: '25%',
      center: true,
    },
    {
      name: 'SKU',
      cell: skuCell,
      width: '15%',
      center: true,
    },
    {
      name: 'Total Stock',
      selector: row => row.quantity,
      width: '15%',
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function handleDeleteStock(stockId) {
    confirm({
      title: "This stock's quantity will be set to 0, are you sure?",
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await RentStockServices.updateStock(stockId, {
            stock: { quantity: 0 },
          });
          fetchStock();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleEditStock(stock) {
    setModalContent(stock);
    setShowModal(true);
  }

  async function fetchStock({ page, q, warehouse_id } = {}) {
    setLoadStatus(true);
    try {
      const { data } = await RentStockServices.getStockList({
        cursor: page,
        limit: 10,
        q,
        warehouse_id,
      });
      const stockData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setStockList(stockData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setShowModal(false);
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/rent-stocks',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['limit', 'page', 'warehouse_id', 'q'])
    );
    navigate({
      pathname: '/rent-stocks',
      search: stringified,
    });
  }

  function filterStock(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/rent-stocks',
      search: stringified,
    });
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    fetchStock({
      page: parsed['page'],
      warehouse_id: parsed['warehouse_id'],
      q: parsed['q'],
    });
    handleCloseModal();
  }

  function handleAfterUpload() {
    resetFilter();
    fetchStock();
  }

  function handleOnChangeSearch({ target: { value } = {} } = {}) {
    setSearchValue(value);
  }

  async function fetchWarehouse() {
    try {
      const {
        data: { data = [] },
      } = await WarehouseServices.getWarehouseList({ status: 'active' });
      setWarehouseList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  async function handleDownloadCsv() {
    const {
      data: { data = [] },
    } = await RentStockServices.getStockReport();
    return data
  }

  useEffect(() => {
    fetchProfile();
    fetchWarehouse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/rent-stocks',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['warehouse_id']) setSelectedWarehouse(parsed['warehouse_id']);
    else setSelectedWarehouse('');

    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    fetchStock({
      page: parsed['page'],
      warehouse_id: parsed['warehouse_id'],
      q: parsed['q'],
    });
  }, [parsed['page'], parsed['warehouse_id'], parsed['q']]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-warehouse">
      <div className="content">
        <div className="pageTitle">Rent Stock</div>
        <Content className="pageContent">
          <Row gutter={[18, 36]}>
            <Col className="mt-1" lg={14}>
              <Search
                value={searchValue}
                onChange={handleOnChangeSearch}
                enterButton
                className="search-product"
                placeholder="Search Product by Name/SKU"
                onSearch={filterStock.bind(this, 'q')}
              />
            </Col>
            <Col lg={{ span: 4, offset: 5 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={setShowModal.bind(this, true)}>
                Add New Stock
              </PdButton>
            </Col>
          </Row>

          <Row gutter={[18, 0]}>
            <Col lg={4} className="mt-1">
              <Select
                className="w-full"
                onChange={filterStock.bind(this, 'warehouse_id')}
                placeholder="Warehouse"
                value={+selectedWarehouse || undefined}>
                {warehouseList.map(({ id = '', title = '' }) => (
                  <Option key={id} value={id}>
                    {startCase(title)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col className="mt-1" lg={4}>
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col lg={{ span: 4, offset: 7 }}>
              <PdCsvUploader
                afterUpload={handleAfterUpload}
                buttonTitle="Upload CSV"
                purpose="rentStock"
              />
            </Col>
            <Col lg={4}>
              <PdCsvDownloader
                datas={handleDownloadCsv}
                filename="stock-homestory"
              />
            </Col>
          </Row>
          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={stockList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <StockCreateEdit
            closeModal={handleCloseCreateEdit}
            stock={modalContent}
            warehouseList={warehouseList}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default { RentStock };
